<script setup>
import { computed, toRefs } from "vue";
import { useTenantTheming } from "@/composables/useTenantTheming";
import { formattedPriceFromCentsWithoutCents } from "@/util/formatter";

const props = defineProps({
  actualCost: { type: Number, default: 0 },
  originalCost: { type: Number, default: undefined },
  discountTitle: { type: String, default: undefined },
  // Legacy: callers that haven't migrated to the cents-based `actualCost` API
  // still pass a pre-formatted dollar string here. Used by the non-discount fallback only.
  priceInDollars: { type: String, default: undefined },
});

const { actualCost, originalCost, discountTitle, priceInDollars } = toRefs(props);

const { isBranded, secondaryColorClass } = useTenantTheming();

const savedAmount = computed(() => originalCost.value - actualCost.value);
const actualCostDisplay = computed(() => formattedPriceFromCentsWithoutCents(actualCost.value));
const originalCostDisplay = computed(() => formattedPriceFromCentsWithoutCents(originalCost.value));
const savedAmountDisplay = computed(() => formattedPriceFromCentsWithoutCents(savedAmount.value));

// Show the discount layout whenever there is a real discount (a lower price with positive savings),
// independent of whether a discount title exists — the title is an optional label, not a gate.
const showDiscount = computed(() => !!originalCost.value && savedAmount.value > 0);
</script>

<template>
  <!-- Discount layout -->
  <div
    v-if="showDiscount"
    data-testid="program-price"
    class="flex items-stretch border border-gray-300 rounded-lg overflow-hidden bg-white mb-2"
  >
    <!-- Left: prices -->
    <div class="flex flex-col justify-center px-4 py-3">
      <span class="text-sm font-semibold text-red-700 line-through decoration-2">{{ originalCostDisplay }}</span>
      <span class="text-3xl font-bold text-black pt-2 pb-1">{{ actualCostDisplay }}</span>
    </div>

    <!-- Dashed divider -->
    <div
      class="border-l border-dashed border-university-secondary/70 self-stretch"
      :class="{ brandingSecondaryBorderSoft: isBranded }"
    ></div>

    <!-- Right: savings -->
    <div
      class="flex flex-col justify-center px-4 py-3 flex-1 min-w-0 bg-university-secondary/15 border-university-secondary"
      :class="{ brandingSecondaryTint: isBranded, brandingSecondaryBorderSoft: isBranded }"
    >
      <span :class="`text-sm font-bold ${secondaryColorClass}`">Save {{ savedAmountDisplay }}</span>
      <span
        v-if="discountTitle"
        data-testid="discount-title"
        :class="`text-xs font-semibold ${secondaryColorClass} break-words line-clamp-4`"
        >{{ discountTitle }}</span
      >
    </div>
  </div>

  <!-- Fallback: existing "Starting at" layout -->
  <div
    v-else
    data-testid="program-price"
    :class="`font-bold uppercase text-base mt-2 mb-4 ${secondaryColorClass}`"
  >
    Starting at {{ priceInDollars ?? actualCostDisplay }}
  </div>
</template>
